import { useNavigate } from "react-router-dom";

import { useBacktestActions } from "@src/hooks/redux";
import { useToastActions } from "@src/hooks/useToasts";
import { tdApi } from "@src/services/api/tdApi";
import { cloneExitPolicy } from "@src/utils/exitPolicyClone";
import { formatBacktestDataFromSource } from "@src/utils/formatters";
import type * as T from "@src/types";
import type {
  ApiBacktest,
  ApiBacktestJob,
  ApiExecution,
  ApiLiveJob,
  ApiRunConfig,
} from "@tradingdroid/frontends-types";

// The engine stores data about the run beside its parameters, each block under a key ending in
// `_observed`. None of it is a setting, and the run form refuses keys it does not send.
const dropObservedBlocks = (config: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(config).filter(([key]) => !key.endsWith("_observed")));

/**
 * The backtest-only entry policies under which the source run departed from the defaults, named as
 * `key: value`. A live execution cannot request them. A row without the observed blocks names none.
 */
const liveDroppedEntryPolicies = (config: ApiRunConfig): string[] => {
  const dropped: string[] = [];
  if (config.fill_at_open) {
    dropped.push("fill_at_open: true");
  }
  // `off` is the drop a run handed no re-entry request executes.
  const reentry = config.same_candle_reentry_observed?.policy;
  if (reentry && reentry !== "off") {
    dropped.push(`same_candle_reentry: ${reentry}`);
  }
  // A hedge or spot run keeps the legacy order under a symmetric policy, and a market-making run has
  // no long or short slot to reverse between, so only this kind of run reversed differently.
  const reversal = config.cross_direction_reversal_observed;
  if (
    reversal?.policy === "symmetric" &&
    reversal.applicability === "non_hedged_futures_directional"
  ) {
    dropped.push("cross_direction_reversal: symmetric");
  }
  return dropped;
};

/**
 * Validates backtest has required data and fetches associated job if needed.
 * @returns Context object with config, job, strategyId, and cleanConfig
 * @throws Error with user-friendly message if validation fails
 */
const getBacktestContext = async (backtest: ApiBacktest) => {
  const backtestConfig = backtest.config;

  if (!backtestConfig) {
    throw new Error("Backtest is missing configuration data");
  }

  if (!backtest.job_id) {
    throw new Error("Backtest has no associated job");
  }

  // Use embedded job if available, otherwise fetch
  let backtestJob = backtest.job;
  if (!backtestJob) {
    const job = await tdApi.getJob(backtest.job_id);
    if (!job) {
      throw new Error(`Job #${backtest.job_id} not found`);
    }
    // Backtest jobs are always backtest type
    backtestJob = job as ApiBacktestJob;
  }

  const strategyId = backtestJob.strategy_id;

  if (!strategyId) {
    throw new Error("Job has no strategy ID");
  }

  // Strip all form-handled fields from config so JSON textarea has no collisions with form inputs
  // Top-level: exchange_name, symbol_name, timeframe, strategy_name (setup form fields)
  // Portfolio: initial_capital, cumulative_investment (form fields)
  // extra_indicators: stored by backtester but not user-editable
  const {
    portfolio,
    exchange_name,
    symbol_name,
    timeframe,
    strategy_name,
    extra_indicators,
    ...configRest
  } = backtestConfig;
  const { initial_capital, cumulative_investment, ...portfolioRest } = portfolio;
  const cleanConfig = dropObservedBlocks({ ...configRest, ...portfolioRest });

  return { backtestConfig, backtestJob, strategyId, cleanConfig };
};

/**
 * Validates execution has required data and fetches associated job if needed.
 * @returns Context object with config, job, strategyId, and cleanConfig
 * @throws Error with user-friendly message if validation fails
 */
const getExecutionContext = async (execution: ApiExecution) => {
  const executionConfig = execution.config;

  if (!executionConfig) {
    throw new Error("Execution is missing configuration data");
  }

  if (!execution.job_id) {
    throw new Error("Execution has no associated job");
  }

  // Use embedded job if available, otherwise fetch
  let executionJob = execution.job;
  if (!executionJob) {
    const job = await tdApi.getJob(execution.job_id);
    if (!job) {
      throw new Error(`Job #${execution.job_id} not found`);
    }
    // Execution jobs are always live type
    executionJob = job as ApiLiveJob;
  }

  const strategyId = executionJob.strategy_id;

  if (!strategyId) {
    throw new Error("Job has no strategy ID");
  }

  // Strip all form-handled fields from config so JSON textarea has no collisions with form inputs
  const {
    portfolio,
    exchange_name,
    symbol_name,
    timeframe,
    strategy_name,
    extra_indicators,
    ...configRest
  } = executionConfig;
  const { initial_capital, cumulative_investment, ...portfolioRest } = portfolio;
  const cleanConfig = dropObservedBlocks({ ...configRest, ...portfolioRest });

  return { executionConfig, executionJob, strategyId, cleanConfig };
};

/**
 * Hook that provides clone/duplicate/create-from-backtest handlers for runs.
 * Extracts all the data transformation + navigation logic out of RunDetailPage.
 */
export const useRunCloneActions = (run: T.ApiRun | null, type: "backtest" | "execution") => {
  const navigate = useNavigate();
  const { showToast } = useToastActions();
  const { setBacktestFromExternal } = useBacktestActions();

  const handleCreateExecution = async () => {
    if (!run || type !== "backtest") return;

    let context;
    try {
      context = await getBacktestContext(run as ApiBacktest);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        message: `Cannot create execution: ${message}`,
        type: "danger",
      });
      return;
    }

    // getBacktestContext validates job exists, so backtestJob.id is guaranteed
    const { backtestConfig, backtestJob, strategyId, cleanConfig } = context;
    // The stored config names fill_at_open when the run used it, and a live execution refuses it.
    const { fill_at_open: _fillAtOpen, ...liveConfig } = cleanConfig;

    // Build execution form data from backtest
    // ONLY pass data we have from the backtest - no made-up defaults
    const executionData: T.CommonSettingsData & { config: string } = {
      strategyId: strategyId,
      exchangeName: backtestConfig.exchange_name,
      instrument: backtestConfig.symbol_name,
      timeframe: backtestConfig.timeframe,
      startDateTime: "", // Executions don't use date range
      endDateTime: "",
      investmentAmount: backtestConfig.portfolio.initial_capital,
      cumulativeInvestment: backtestConfig.portfolio.cumulative_investment,
      config: JSON.stringify(liveConfig, null, 2),
      // Include origin metadata for traceability - link execution back to source backtest
      origin: {
        type: "backtest",
        backtest_id: run.id,
        backtest_job_id: backtestJob.id,
      },
    };

    setBacktestFromExternal(executionData);
    const dropped = liveDroppedEntryPolicies(backtestConfig);
    if (dropped.length > 0) {
      showToast({
        message:
          `The source backtest ran with ${dropped.join(" and ")}. A live execution cannot use ` +
          "these backtest-only entry policies, so the execution form leaves them out, and the " +
          "execution's entries can differ from the backtest's.",
        type: "warning",
      });
    }
    navigate("/execution/new?loadFrom=backtest");
  };

  const handleDuplicateBacktest = async () => {
    if (!run || type !== "backtest") return;

    let context;
    try {
      context = await getBacktestContext(run as ApiBacktest);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        message: `Cannot duplicate: ${message}`,
        type: "danger",
      });
      return;
    }

    const { backtestConfig, backtestJob, strategyId, cleanConfig } = context;

    // An older row can name neither exit policy key. The engine resolves such a config to legacy,
    // while the API stamps its current policy on a new backtest that requests neither, so the
    // helper pins the legacy pair into the copy, where the form's JSON shows it before submit.
    // The stored config names the re-entry mode the run executed only inside its observed block.
    const reentry = backtestConfig.same_candle_reentry_observed;
    // The cross-direction reversal policy is likewise only in its observed block. The copy carries
    // the source job's request, not the resolved policy: a request of null means the job omitted
    // the key, and the copy omits it too, so an explicit legacy stays told apart from no request.
    const reversal = backtestConfig.cross_direction_reversal_observed;
    const { config: duplicateConfig, policy } = cloneExitPolicy("backtest", {
      ...cleanConfig,
      ...(reentry?.policy && { same_candle_reentry: reentry.policy }),
      ...(reentry?.unresolved_policy && {
        same_candle_reentry_unresolved: reentry.unresolved_policy,
      }),
      ...(reversal?.requested && { cross_direction_reversal: reversal.requested }),
    });

    // Build backtest form data from current backtest
    const backtestData: T.CommonSettingsData & { config: string } = {
      strategyId: strategyId,
      exchangeName: backtestConfig.exchange_name,
      instrument: backtestConfig.symbol_name,
      timeframe: backtestConfig.timeframe,
      startDateTime: backtestJob.from_ts ?? "",
      endDateTime: backtestJob.end_ts ?? "",
      investmentAmount: backtestConfig.portfolio.initial_capital,
      cumulativeInvestment: backtestConfig.portfolio.cumulative_investment,
      config: JSON.stringify(duplicateConfig, null, 2),
      // Include origin metadata for traceability - link duplicate back to source
      origin: {
        type: "duplicate",
        source_job_id: backtestJob.id,
        backtest_id: run.id,
      },
    };

    setBacktestFromExternal(formatBacktestDataFromSource(backtestData));
    if (policy.kind === "flag_only" && policy.versionFloats) {
      showToast({
        message:
          "The source backtest sets exit_arbitration: true without a pinned exit_semantics_version. " +
          "The duplicate keeps it that way, so the engine resolves the version when the new " +
          "backtest runs, and it can use a later version than the source did.",
        type: "warning",
      });
    }
    navigate("/backtest/new?loadFrom=duplicate");
  };

  const handleDuplicateExecution = async () => {
    if (!run || type !== "execution") return;

    let context;
    try {
      context = await getExecutionContext(run as ApiExecution);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast({
        message: `Cannot duplicate: ${message}`,
        type: "danger",
      });
      return;
    }

    const { executionConfig, executionJob, strategyId, cleanConfig } = context;

    // Build execution form data from current execution
    const executionData = formatBacktestDataFromSource({
      strategyId: strategyId,
      exchangeName: executionConfig.exchange_name,
      instrument: executionConfig.symbol_name,
      timeframe: executionConfig.timeframe,
      startDateTime: "", // Executions don't use date range
      endDateTime: "",
      investmentAmount: executionConfig.portfolio.initial_capital,
      cumulativeInvestment: executionConfig.portfolio.cumulative_investment,
      config: JSON.stringify(cleanConfig, null, 2),
      // Include execution-specific settings from job config
      exchangeEnvironment: executionJob.config.sandbox ? "qa" : "prod",
      infisicalKeyPath: executionJob.config.secrets_provider_config?.key_path ?? "",
      infisicalSecretPath: executionJob.config.secrets_provider_config?.secret_path ?? "",
      infisicalPasswordPath: executionJob.config.secrets_provider_config?.password_path ?? "",
      // Include origin metadata for traceability - link duplicate back to source
      origin: {
        type: "duplicate",
        source_job_id: executionJob.id,
        execution_id: run.id,
      },
    });

    setBacktestFromExternal(executionData);
    navigate("/execution/new?loadFrom=duplicate");
  };

  return { handleCreateExecution, handleDuplicateBacktest, handleDuplicateExecution };
};
